import { parseEntityRef } from '@backstage/catalog-model';
import type { ScaffolderTask } from '@backstage/plugin-scaffolder-react';
import { userDisplayName } from '../projectsNav/userBlock';
import { relativeTime } from '../home/relativeTime';
import { frenchDuration } from '../taskProjectBanner/deletionTask';
import { taskProject } from '../taskProjectBanner/taskProject';
import { templateAppTitle } from '../shared/appTitle';
import type { AppTypeDetail } from '../shared/appTypes';
import { LEGACY_APP_TITLE } from '../shared/projectIdentity';
import { roleOfTemplate } from '../shared/templateRoles';

/**
 * What the « Tâches » page and the sidebar badge read from a scaffolder task
 * of the list (`GET /api/scaffolder/v2/tasks`). Pure: `TasksPage.tsx` and
 * `TasksNavItem.tsx` render it.
 *
 * WHAT THE LIST GIVES. Each task comes with its `spec` (steps, parameters,
 * template info, launching user), `status`, `createdAt`, `lastHeartbeatAt`
 * and `createdBy` — not its per-step state, nor its events (scaffolder-backend
 * 4.2 `DatabaseTaskStore.list`). So no « étape 6/9 » and no « 10 à
 * reprendre » here: each would cost one more request per task. The reason of
 * a failure is the one exception, read for the failed rows on screen only
 * (`taskFailure.ts`).
 */

/** What a task does, as its « Type » column says it. */
export type TaskKind =
  | 'creation'
  | 'deletion'
  | 'simulation'
  | 'wake'
  | 'sleep'
  | 'promote'
  | 'configure'
  | 'restore'
  | 'other';

/** A task's state on the page. */
export type TaskState = 'running' | 'completed' | 'failed' | 'cancelled';

/** The « État » filter: every task, the running ones, the failed ones. */
export type TaskStatusFilter = 'all' | 'running' | 'failed';

/** The admin's « Mes tâches / Toutes » choice. */
export type TaskOwnerFilter = 'mine' | 'all';

/** The kinds of our own templates, each with its label and small line. */
export type TaskKindFilter = Exclude<TaskKind, 'other'>;

/**
 * The type chips (« Création · Configuration · Promotion · Réveil / sommeil ·
 * Suppression », mockup « V1 », 2026-10-18): a wake and a sleep share one
 * chip, a simulation is a deletion that changes nothing and goes with the
 * deletions, any other template is « Autres ».
 */
export type TaskTypeFilter =
  | 'creation'
  | 'configure'
  | 'promote'
  | 'power'
  | 'deletion'
  | 'other';

export const KIND_TYPES: Record<TaskKind, TaskTypeFilter> = {
  creation: 'creation',
  deletion: 'deletion',
  simulation: 'deletion',
  wake: 'power',
  sleep: 'power',
  promote: 'promote',
  configure: 'configure',
  // No chip of its own (the chips of mockup « V1 » are unchanged): with
  // « Autres », under its own label.
  restore: 'other',
  other: 'other',
};

/**
 * The scaffolder's task status (`TaskStatus`, plugin-scaffolder-common) as
 * the page names it. `skipped` is a step status the type shares; a task
 * never carries it, and it would read « Terminée » if one did.
 */
const STATES: Record<ScaffolderTask['status'], TaskState> = {
  open: 'running',
  processing: 'running',
  completed: 'completed',
  skipped: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
};

export const STATE_LABELS: Record<TaskState, string> = {
  running: 'En cours',
  completed: 'Terminée',
  failed: 'Échec',
  cancelled: 'Annulée',
};

/** The pill's tone (`.waadoo-deletion-report__pill[data-tone]`, styles.css). */
export const STATE_TONES: Record<
  TaskState,
  'info' | 'success' | 'danger' | 'warning'
> = {
  running: 'info',
  completed: 'success',
  failed: 'danger',
  cancelled: 'warning',
};

export const KIND_LABELS: Record<TaskKindFilter, string> = {
  creation: 'Création',
  deletion: 'Suppression',
  simulation: 'Simulation',
  wake: 'Réveil',
  sleep: 'Mise en sommeil',
  promote: 'Promotion',
  configure: 'Configuration',
  restore: 'Restauration à un instant',
};

/**
 * The small line under the type; a creation's names the application of the
 * template when another type designates it (`kindDetail`).
 */
export const KIND_DETAILS: Record<TaskKindFilter, string> = {
  creation: `projet ${LEGACY_APP_TITLE}`,
  deletion: 'suppression réelle',
  simulation: 'suppression, sans rien modifier',
  wake: 'environnement permanent',
  sleep: 'environnement permanent',
  promote: 'branche permanente',
  configure: 'environnement permanent',
  restore: 'environnement permanent',
};

export function taskState(task: ScaffolderTask): TaskState {
  return STATES[task.status];
}

export function isRunning(task: ScaffolderTask): boolean {
  return taskState(task) === 'running';
}

/**
 * The kind of a task, from the role of its template (`roleOfTemplate`: the
 * embedded Odoo descriptor's and the generic templates, and those the
 * descriptors `appTypes` designate). A deletion is a
 * simulation unless `dryRun` is exactly `false` — the reading of the
 * deletion actions themselves, already the banner's (`taskProject.ts`).
 */
export function taskKind(
  task: ScaffolderTask,
  appTypes: readonly AppTypeDetail[] = [],
): TaskKind {
  const role = roleOfTemplate(task.spec.templateInfo?.entityRef, appTypes);
  if (role === 'create') return 'creation';
  if (role === 'delete') {
    return task.spec.parameters.dryRun === false ? 'deletion' : 'simulation';
  }
  return role ?? 'other';
}

/** One row of the table. */
export interface TaskRow {
  id: string;
  kind: TaskKind;
  /** « Création », …, or the title of any other template. */
  label: string;
  /** The small line under the label; none for another template. */
  detail?: string;
  /** The project, for the Odoo project and environment templates. */
  project?: string;
  /**
   * The permanent environment the small line names — a wake's, a sleep's, a
   * change of configuration's, a promotion's target —, for its tier dot.
   */
  environment?: string;
  /** A promotion's source branch, for the search. */
  source?: string;
  state: TaskState;
  /** The launching user's entity ref, from the task spec. */
  userRef?: string;
  /** Their display name, else the name part of their ref. */
  userName?: string;
  createdAt: string;
  /** The task's last heartbeat, as the list gives it. */
  lastHeartbeatAt?: string;
  /** Milliseconds, when the task's fields allow it (`taskDurationMs`). */
  durationMs?: number;
}

/**
 * The template of a task that is neither of ours: its title as the task
 * stored it, else the name of its ref, else « Tâche ».
 */
function templateTitle(task: ScaffolderTask): string {
  const info = task.spec.templateInfo;
  const title = info?.entity?.metadata.title;
  if (title) return title;
  if (info?.entityRef) {
    try {
      return parseEntityRef(info.entityRef).name;
    } catch {
      // A ref the catalog would not parse: the generic word below.
    }
  }
  return 'Tâche';
}

/**
 * How long the task ran, in milliseconds:
 *  - running: since its creation, at `now` — once claimed (`processing`);
 *    a task still waiting (`open`) has none;
 *  - ended: from its creation to its last heartbeat. The scaffolder beats
 *    every second while it processes a task and leaves the field alone when
 *    it completes it (`StorageTaskBroker`, `DatabaseTaskStore.completeTask`),
 *    so the end is at most a second early. None without a heartbeat — a
 *    task cancelled before it was claimed.
 * A retried task keeps its first `createdAt`, so its duration includes the
 * time before the retry.
 */
export function taskDurationMs(
  task: ScaffolderTask,
  now: number,
): number | undefined {
  const start = Date.parse(task.createdAt);
  if (Number.isNaN(start)) return undefined;
  if (task.status === 'processing') return Math.max(0, now - start);
  if (taskState(task) === 'running' || !task.lastHeartbeatAt) {
    return undefined;
  }
  const end = Date.parse(task.lastHeartbeatAt);
  return Number.isNaN(end) ? undefined : Math.max(0, end - start);
}

/**
 * One row of the table; `appTypes` the descriptors, for the templates only
 * an AppType designates (`TasksPage` reads them when the list has one).
 */
export function taskRow(
  task: ScaffolderTask,
  now: number,
  appTypes: readonly AppTypeDetail[] = [],
): TaskRow {
  const kind = taskKind(task, appTypes);
  const userRef = task.spec.user?.ref;
  const about = taskProject(task, appTypes);
  return {
    id: task.id,
    kind,
    label: kind === 'other' ? templateTitle(task) : KIND_LABELS[kind],
    detail: kindDetail(task, kind, appTypes),
    project: about?.project,
    environment: about?.environment,
    source: about?.source,
    state: taskState(task),
    userRef,
    userName: userRef
      ? userDisplayName(
          task.spec.user?.entity?.spec?.profile?.displayName,
          userRef,
        )
      : undefined,
    createdAt: task.createdAt,
    lastHeartbeatAt: task.lastHeartbeatAt,
    durationMs: taskDurationMs(task, now),
  };
}

/**
 * The small line under the type: the environment for a wake, a sleep or a
 * change of configuration (« preprod »), the two branches of a promotion (« dev → preprod »),
 * « projet <application> » for a creation (`templateAppTitle`: Odoo for the
 * Odoo creation template, the designating type's title, « projet » alone when
 * several types designate it), else the kind's own line; none for another
 * template.
 */
function kindDetail(
  task: ScaffolderTask,
  kind: TaskKind,
  appTypes: readonly AppTypeDetail[],
): string | undefined {
  if (kind === 'other') return undefined;
  if (kind === 'creation') {
    const app = templateAppTitle(task.spec.templateInfo?.entityRef, appTypes);
    return app ? `projet ${app}` : 'projet';
  }
  if (kind === 'promote') {
    const about = taskProject(task, appTypes);
    return about?.source && about.environment
      ? `${about.source} → ${about.environment}`
      : KIND_DETAILS[kind];
  }
  if (kind === 'wake' || kind === 'sleep' || kind === 'configure') {
    return taskProject(task, appTypes)?.environment ?? KIND_DETAILS[kind];
  }
  if (kind === 'restore') {
    const environment =
      taskProject(task, appTypes)?.environment ?? KIND_DETAILS[kind];
    const operation = task.spec.parameters.operation;
    if (operation === 'validate') return `${environment} · validation`;
    if (operation === 'cancel') return `${environment} · annulation`;
    return environment;
  }
  return KIND_DETAILS[kind];
}

/** The duration as the column shows it: « 58 s », « 1 min 02 s ». */
export function durationText(ms: number): string {
  return frenchDuration(ms);
}

/** The rows the admin's « Mes tâches / Toutes » choice keeps. */
export function ownedRows<T extends TaskRow>(
  rows: T[],
  owner: TaskOwnerFilter,
  userRef: string | undefined,
): T[] {
  return owner === 'all' ? rows : rows.filter(row => row.userRef === userRef);
}

/** The rows of one type chip, or all of them without one. */
export function typeRows<T extends TaskRow>(
  rows: T[],
  type: TaskTypeFilter | undefined,
): T[] {
  return type ? rows.filter(row => KIND_TYPES[row.kind] === type) : rows;
}

/** Lower case, without accents: « Réveil » is found by « reveil ». */
function folded(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr-FR');
}

/**
 * The rows the search field keeps (« Projet, branche… »): every word typed
 * is found in the project, the environment or branches the small line names,
 * or the type's label. Accents and case are ignored.
 */
export function searchRows<T extends TaskRow>(rows: T[], query: string): T[] {
  const words = folded(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return rows;
  return rows.filter(row => {
    const text = folded(
      [row.project, row.environment, row.source, row.detail, row.label]
        .filter(Boolean)
        .join(' '),
    );
    return words.every(word => text.includes(word));
  });
}

/**
 * How long after a creation ended its project may still be missing from the
 * catalog's list: the shared list is kept 30 s (`projectList.ts`) and the
 * catalog registers the new entity on its own schedule.
 */
export const CREATION_GRACE_MS = 15 * 60 * 1000;

/**
 * Whether a row's project is gone from the reader's catalog list — « supprimé »
 * for an admin, « plus accessible » for anyone else (`TasksPage.tsx`). Only once
 * the project list is known (`known`), and never for:
 *  - a task without a project;
 *  - a creation that did not complete: its project never existed;
 *  - a creation ended less than {@link CREATION_GRACE_MS} ago: the list may
 *    not have it yet.
 * The list is the reader's own: a non-admin sees the projects of their
 * `prj-<project>` groups, so a project they left is gone from it too —
 * hence the neutral word for them.
 */
export function isProjectGone(
  row: TaskRow,
  known: ReadonlySet<string> | undefined,
  now: number,
): boolean {
  if (!row.project || !known || known.has(row.project)) return false;
  if (row.kind === 'creation') {
    if (row.state !== 'completed') return false;
    const end = Date.parse(row.lastHeartbeatAt ?? row.createdAt);
    if (now - end < CREATION_GRACE_MS) return false;
  }
  return true;
}

export function statusRows<T extends TaskRow>(
  rows: T[],
  status: TaskStatusFilter,
): T[] {
  if (status === 'running') return rows.filter(row => row.state === 'running');
  if (status === 'failed') return rows.filter(row => row.state === 'failed');
  return rows;
}

/** The counts of the « État » chips, over the rows they would filter. */
export function statusCounts(
  rows: TaskRow[],
): Record<TaskStatusFilter, number> {
  return {
    all: rows.length,
    running: statusRows(rows, 'running').length,
    failed: statusRows(rows, 'failed').length,
  };
}

const LOCALE = 'fr-FR';

/** The calendar day of `at` in `timeZone`, as a comparable string. */
function dayKey(at: number, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(at);
}

/** The calendar day before `today` (« YYYY-MM-DD »), by date arithmetic:
 * `now` minus 24 h would miss it for an hour after a 23-hour day. */
function dayBefore(today: string): string {
  const [year, month, date] = today.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date - 1))
    .toISOString()
    .slice(0, 10);
}

/**
 * The header of a day of the list: « Aujourd’hui », « Hier », then
 * « vendredi 16 octobre » — with the year when it is not the current one.
 * `at` is any time of that day. In the browser's time zone; `timeZone` is for
 * the tests.
 */
export function dayLabel(at: number, now: number, timeZone?: string): string {
  const day = dayKey(at, timeZone);
  const today = dayKey(now, timeZone);
  if (day === today) return 'Aujourd’hui';
  if (day === dayBefore(today)) return 'Hier';
  return new Intl.DateTimeFormat(LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: day.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
    timeZone,
  }).format(at);
}

const HOUR_MS = 60 * 60 * 1000;

/**
 * When a task started, under its day: « il y a 5 min » (« maintenant » under
 * a minute; `relativeTime`, home/relativeTime.ts) within the hour, else the
 * time, « 13:33 ».
 */
export function timeText(iso: string, now: number, timeZone?: string): string {
  const at = Date.parse(iso);
  if (now - at < HOUR_MS) return relativeTime(at, now, LOCALE);
  return new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(at);
}

/** The rows of one day, newest first as the list. */
export interface TaskDay<T extends TaskRow = TaskRow> {
  /** « YYYY-MM-DD », in the time zone. */
  key: string;
  label: string;
  rows: T[];
}

/**
 * The rows grouped by the day they started, in their order (the scaffolder
 * lists newest first, so each day comes once).
 */
export function groupByDay<T extends TaskRow>(
  rows: T[],
  now: number,
  timeZone?: string,
): TaskDay<T>[] {
  const days: TaskDay<T>[] = [];
  for (const row of rows) {
    const at = Date.parse(row.createdAt);
    const key = dayKey(at, timeZone);
    const last = days[days.length - 1];
    if (last?.key === key) {
      last.rows.push(row);
    } else {
      days.push({ key, label: dayLabel(at, now, timeZone), rows: [row] });
    }
  }
  return days;
}

/**
 * When a task ended, as far as the list tells: its last heartbeat, else its
 * creation (see `taskDurationMs`). For the badge's « failed since the last
 * visit ».
 */
export function taskEndedAt(task: ScaffolderTask): number {
  const end = task.lastHeartbeatAt
    ? Date.parse(task.lastHeartbeatAt)
    : Number.NaN;
  return Number.isNaN(end) ? Date.parse(task.createdAt) : end;
}

/**
 * The newest time a list of tasks shows — the latest creation or heartbeat,
 * both written by the scaffolder's database clock. What a visit of the page
 * records (`lastVisit.ts`), so that the badge compares a task's end with a
 * time of the same clock, never with the browser's. Undefined for an empty
 * list.
 */
export function newestTaskTime(tasks: ScaffolderTask[]): number | undefined {
  let newest: number | undefined;
  for (const task of tasks) {
    for (const iso of [task.createdAt, task.lastHeartbeatAt]) {
      const at = iso ? Date.parse(iso) : Number.NaN;
      if (!Number.isNaN(at) && (newest === undefined || at > newest)) {
        newest = at;
      }
    }
  }
  return newest;
}

/** What the sidebar badge shows. */
export interface TasksBadge {
  running: number;
  /** Failed tasks that ended after `lastVisit` (`newestTaskTime`). */
  newFailures: number;
}

export function tasksBadge(
  tasks: ScaffolderTask[],
  lastVisit: number | undefined,
): TasksBadge {
  return {
    running: tasks.filter(isRunning).length,
    newFailures:
      lastVisit === undefined
        ? 0
        : tasks.filter(
            task =>
              taskState(task) === 'failed' && taskEndedAt(task) > lastVisit,
          ).length,
  };
}
